// ConfirmDialog — Polish #24 shared confirm modal used to gate destructive
// bulk actions (Mark-as-spam on Standup, etc). Matches ShortcutHelp's
// backdrop + card treatment so there's one modal look-and-feel across
// Standup and Center.
//
// Usage:
//
//   <ConfirmDialog
//     open={open}
//     title="Mark 8 threads as spam?"
//     body="They'll be hidden from the Pile. You can reveal them later with the 'N likely-spam hidden' toggle."
//     confirmLabel="Mark as spam"
//     danger
//     onCancel={() => setOpen(false)}
//     onConfirm={() => { run(); setOpen(false); }}
//   />
//
// Keyboard:
//   Esc        → cancel
//   Enter      → confirm (only when the confirm button has focus, which it
//                gets automatically on open)
//
// The modal is "dumb" about what the action is — parent owns the actual
// mutation call. This is deliberate so the same modal can sit in front of
// any destructive action: bulk spam, delete account, etc.

import { useEffect, useRef } from 'react';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** Body text. Can include plain strings — keep it short and scannable. */
  body: string;
  /** Label for the primary (confirm) button. */
  confirmLabel: string;
  /** Label for the secondary (cancel) button. Defaults to "Cancel". */
  cancelLabel?: string;
  /**
   * When true, renders the confirm button in the clay (red-ish) destructive
   * treatment. Off by default — dialogs guarding benign actions should not
   * look scary.
   */
  danger?: boolean;
  /**
   * While true the dialog is frozen: buttons disable, Enter/Esc still work
   * but don't fire handlers. Use this to keep the modal visible while the
   * mutation is in flight so Kimi doesn't see a flash of un-modal state.
   */
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  danger = false,
  busy = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  // Autofocus the confirm button on open so Enter can fire it and Esc
  // cancels (Esc is on window below). Don't focus when busy so the in-flight
  // spinner doesn't shift focus in the middle of a mutation.
  useEffect(() => {
    if (!open || busy) return;
    // Defer so the button is in the DOM. requestAnimationFrame is enough.
    const raf = requestAnimationFrame(() => {
      confirmRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open, busy]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!busy) onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="confirm-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      onClick={busy ? undefined : onCancel}
    >
      <div className="confirm-dialog-card" onClick={(e) => e.stopPropagation()}>
        <h2 id="confirm-dialog-title" className="confirm-dialog-title">
          {title}
        </h2>
        <p className="confirm-dialog-body">{body}</p>
        <div className="confirm-dialog-actions">
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`btn confirm-dialog-confirm${danger ? ' is-danger' : ''}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
